import { ChangeEvent, MouseEvent, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { passwordValidationSchema, ValidationErrors } from 'src/constants/validations';
import { useAuthenticationApi } from '../api';

export const useSetPasswordViewController = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formValues, setFormValues] = useState({ password: '' });
  const [linkInvalid, setLinkInvalid] = useState(false);

  const navigate = useNavigate();
  const api = useAuthenticationApi();
  const location = useLocation();

  const code = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('code');
  }, []);

  const validate = async (values: typeof formValues) => {
    try {
      await passwordValidationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSetPassword = async () => {
    const isValid = await validate(formValues);
    if (isValid) {
      code &&
        api
          .setPassword({ token: code, password: formValues.password })
          .then(() => {
            navigate('/login');
          })
          .catch(e => {
            if (e.error === 'validation') {
              setLinkInvalid(true);
            }
          });
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  const navigateToForgotPassword = () => {
    navigate('/forgot-password');
  };

  return {
    showPassword,
    errors,
    formValues,
    linkInvalid,
    handleChange,
    handleClickShowPassword,
    handleMouseDownPassword,
    handleSetPassword,
    navigateToLogin,
    navigateToForgotPassword,
  };
};
